import React from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";
import ScrollToTop from "components/ScrollToTop";
import ErrorBoundary from "components/ErrorBoundary";
import { useGoogleAnalytics } from './hooks/useGoogleAnalytics';
import { useAuth } from './contexts/AuthContext';
import NotFound from "pages/NotFound";
import Home from './pages/home';
import OnboardingAgent from './pages/onboarding-agent';
import ManagerAgentInterface from './pages/manager-agent-interface';
import MarketingAgentInterface from './pages/marketing-agent-interface';
import Dashboard from './pages/dashboard';
import Login from './pages/login';
import Register from './pages/register';
import Pricing from './pages/pricing';
import SubscriptionSuccess from './pages/subscription-success';
import BillingManagement from './pages/billing-management';
import AgentManagement from './pages/agent-management';
import AnalyticsDashboard from './pages/analytics-dashboard';
import NotificationPreferences from './pages/notification-preferences';
import PersonalProductivityAgentInterface from './pages/personal-productivity-agent-interface';
import FeedbackAnalyticsAgentInterface from './pages/feedback-analytics-agent-interface';
import OnboardingAgentInterface from './pages/onboarding-agent-interface';
import AlertManagement from './pages/alert-management';
import TeamManagement from './pages/team-management';
import AdminDashboard from './pages/admin-dashboard';
import SSOSettings from './pages/sso-settings';
import SSOLogin from './pages/sso-login';
import SecuritySettings from './pages/security-settings';
import DataPrivacySettings from './pages/data-privacy-settings';
import ApiMonitoringDashboard from './pages/api-monitoring-dashboard';
import ForgotPassword from './pages/forgot-password';
import ResetPassword from './pages/reset-password';

// Protected Route wrapper
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Public Route wrapper (redirects to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { user, loading, isSessionVerified } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  // Wait for session verification before redirecting
  // This ensures user object, profile, and organization data are fully populated
  if (user && !isSessionVerified) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }
  
  if (user && isSessionVerified) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Wrapper component to use analytics hook inside Router context
const AnalyticsWrapper = ({ children }) => {
  useGoogleAnalytics();
  return children;
};

const Routes = () => {
  return (
    <BrowserRouter>
      <AnalyticsWrapper>
        <ErrorBoundary>
        <ScrollToTop />
        <RouterRoutes>
          {/* Public Routes */}
          <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
          <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/sso-login" element={<SSOLogin />} />
          
          {/* Protected Routes */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/onboarding-agent" element={<ProtectedRoute><OnboardingAgent /></ProtectedRoute>} />
          <Route path="/manager-agent-interface" element={<ProtectedRoute><ManagerAgentInterface /></ProtectedRoute>} />
          <Route path="/marketing-agent-interface" element={<ProtectedRoute><MarketingAgentInterface /></ProtectedRoute>} />
          <Route path="/personal-productivity-agent-interface" element={<ProtectedRoute><PersonalProductivityAgentInterface /></ProtectedRoute>} />
          <Route path="/feedback-analytics-agent-interface" element={<ProtectedRoute><FeedbackAnalyticsAgentInterface /></ProtectedRoute>} />
          <Route path="/onboarding-agent-interface" element={<ProtectedRoute><OnboardingAgentInterface /></ProtectedRoute>} />
          <Route path="/subscription-success" element={<ProtectedRoute><SubscriptionSuccess /></ProtectedRoute>} />
          <Route path="/billing-management" element={<ProtectedRoute><BillingManagement /></ProtectedRoute>} />
          <Route path="/agent-management" element={<ProtectedRoute><AgentManagement /></ProtectedRoute>} />
          <Route path="/analytics-dashboard" element={<ProtectedRoute><AnalyticsDashboard /></ProtectedRoute>} />
          <Route path="/notification-preferences" element={<ProtectedRoute><NotificationPreferences /></ProtectedRoute>} />
          <Route path="/security-settings" element={<ProtectedRoute><SecuritySettings /></ProtectedRoute>} />
          <Route path="/data-privacy-settings" element={<ProtectedRoute><DataPrivacySettings /></ProtectedRoute>} />
          <Route path="/alert-management" element={<ProtectedRoute><AlertManagement /></ProtectedRoute>} />
          <Route path="/team-management" element={<ProtectedRoute><TeamManagement /></ProtectedRoute>} />
          <Route path="/admin-dashboard" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/sso-settings" element={<ProtectedRoute><SSOSettings /></ProtectedRoute>} />
          <Route path="/api-monitoring-dashboard" element={<ProtectedRoute><ApiMonitoringDashboard /></ProtectedRoute>} />
          
          <Route path="*" element={<NotFound />} />
        </RouterRoutes>
        </ErrorBoundary>
      </AnalyticsWrapper>
    </BrowserRouter>
  );
};

export default Routes;
